import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Input from 'views/ui/form_elements/input';
import { brandNeutralColors } from 'ui_system/compass/utils/theme';
import { capitalize } from 'lib/string_utils';
import { useIntl, FormattedMessage } from 'react-intl';

const initialState = {
  firstName: '',
  lastName: '',
};

export const GuestLabelRow = styled.div`
  padding-bottom: 0.5rem;
`;

export const GuestLabel = styled.span`
  font-size: 16px;
`;

export const TicketLabel = styled(GuestLabel)`
  font-weight: 400;
  color: ${brandNeutralColors.text.secondary};
  margin-left: 10px;
`;

/**
 * GuestNameInput renders a first and last name text input to collect additional guest information
 *
 * @param {number} index
 * @param {string} selectedTicket e.g. 'Adult'
 * @param {function} onChange
 * @param {number} unitTypeId PricingUnitType#id
 * @param {string} unitTypeName PricingUnitType#noun_singular
 * @param {boolean} hasMultipleUnits
 */
const GuestNameInput = ({
  index,
  selectedTicket,
  onChange,
  unitTypeId,
  unitTypeName,
  hasMultipleUnits,
  dataTestId,
}) => {
  const intl = useIntl();
  const [{ firstName, lastName }, setState] = useState(initialState);

  const clearState = () => {
    setState({ ...initialState });

    // XXX manually clear out the state
    onChange(index, '', 'first_name', unitTypeId);
    onChange(index, '', 'last_name', unitTypeId);
  };

  // Clear fields when a different ticket type is selected
  useEffect(() => {
    clearState();
  }, [selectedTicket]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (fieldName, value) => {
    setState((prevState) => ({ ...prevState, [fieldName]: value }));

    // Persist additional guest names to legacy state system in CheckoutPage + CheckoutNavigationController
    const nameField = fieldName === 'firstName' ? 'first_name' : 'last_name';
    onChange(index, value, nameField, unitTypeId);
  };

  return (
    <div className="row" key={index} data-testid={dataTestId}>
      <GuestLabelRow>
        <GuestLabel data-testid="guest-label">
          {hasMultipleUnits && selectedTicket ? (
            <FormattedMessage
              id="GUEST_NUMBER_WITH_UNIT_TYPE"
              defaultMessage="Guest {number} ({unitType})"
              values={{
                number: index + 2,
                unitType: capitalize(unitTypeName),
              }}
            />
          ) : (
            <FormattedMessage
              id="GUEST_NUMBER"
              defaultMessage="Guest {number}"
              values={{ number: index + 2 }}
            />
          )}
        </GuestLabel>
      </GuestLabelRow>
      <div className="double-column">
        <div className="column">
          <Input
            value={firstName}
            name="firstName"
            placeholder={intl.formatMessage({
              id: 'FIRST_NAME',
              defaultMessage: 'First name',
            })}
            maxLength="150"
            onChange={handleChange}
          />
        </div>
        <div className="column">
          <Input
            value={lastName}
            name="lastName"
            placeholder={intl.formatMessage({
              id: 'LAST_NAME',
              defaultMessage: 'Last name',
            })}
            maxLength="150"
            onChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

GuestNameInput.propTypes = {
  index: PropTypes.number,
  selectedTicket: PropTypes.string,
  onChange: PropTypes.func,
  unitTypeId: PropTypes.number,
  unitTypeName: PropTypes.string,
};

export default GuestNameInput;
